import type { CustomNextRouter } from 'src/shared/lib/history/types';

import {
  abilityQueryToApiMap,
  availabilityQueryToApiMap,
  popularQueryToApiMap,
  priceQueryToApiMap,
  queryParamToRequestParamMap,
  styleQueryToApiMap,
  subjectQueryToApiMap,
  toolQueryToApiMap,
} from './maps';
import type { FilterQueryParam } from './types';

type QueryValue = string | string[] | undefined;

const toArray = (value?: QueryValue) => {
  if (!value) {
    return [];
  }

  const arr = Array.isArray(value)
    ? value
    : [value];

  return arr
    .flatMap((item) => item.split(','))
    .map((item) => item.trim())
    .filter(Boolean);
};

export const hasPsychicFilterQueryParams = (query: Record<string, any> | undefined | null) => {
  if (!query) {
    return false;
  }

  return Object.keys(query).some((key) => key.toLowerCase().startsWith('pf_'));
};

const joinCsvMapped = (
  rawValues: string[],
  map: Partial<Record<string, string>>,
) => {
  const mapped = rawValues
    .reduce((acc, value) => {
      const mappedValue = map[value];

      if (mappedValue) {
        acc.add(mappedValue);
      }

      return acc;
    }, new Set<string>());

  return Array.from(mapped).join(',');
};

const applyCommonFilter = (
  filter: FilterQueryParam,
  mapper: Partial<Record<string, string>>,
  extra: {
    query: Record<string, QueryValue>;
    counter: number;
  },
) => {
  const requestParam = queryParamToRequestParamMap[filter];

  if (!requestParam || !extra.query[filter]) {
    return {};
  }

  const array = toArray(extra.query[filter]);

  // eslint-disable-next-line no-param-reassign
  extra.counter += array.length;

  return {
    [requestParam]: joinCsvMapped(array, mapper),
  };
};

export const mapPsychicFiltersFromQuery = (router: CustomNextRouter) => {
  const { query } = router;

  if (!query || !Object.keys(query).length || !router.asPath.includes('?')) {
    return {};
  }

  const commonFilterCommonParameters = { query, counter: 0 };
  const SearchOptions: Record<string | number, string> = {
    ...applyCommonFilter('pf_subject', subjectQueryToApiMap, commonFilterCommonParameters),
    ...applyCommonFilter('pf_ability', abilityQueryToApiMap, commonFilterCommonParameters),
    ...applyCommonFilter('pf_style', styleQueryToApiMap, commonFilterCommonParameters),
    ...applyCommonFilter('pf_tool', toolQueryToApiMap, commonFilterCommonParameters),
    ...applyCommonFilter('pf_price', priceQueryToApiMap, commonFilterCommonParameters),
    ...applyCommonFilter('pf_popular', popularQueryToApiMap, commonFilterCommonParameters),
  };

  const pfAvailability = toArray(query.pf_availability);
  pfAvailability.forEach((value) => {
    const mappedKey = availabilityQueryToApiMap[value];

    if (mappedKey) {
      commonFilterCommonParameters.counter++;
      SearchOptions[mappedKey] = 'yes';
    }
  });

  const searchOptionsLength = Object.keys(SearchOptions).length;

  if (searchOptionsLength) {
    return { selectedFilters: { SearchOptions }, __counter: commonFilterCommonParameters.counter };
  }

  return {};
};
